import React, { useState } from 'react';

import CloseIcon from '@mui/icons-material/Close';
import DoneIcon from '@mui/icons-material/Done';
import { Box, Button, CircularProgress, Popover, TextField } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import callAPI from '../../Utilities/callAPI';
import { classes } from '../styles';

import type { Dispatch } from 'react';

import type { TableActionItemAction } from '../types';

const AddNoteWithSubjectLine = <TableData,>({
  dispatch,
  isOpen,
  anchorEl,
  itemId,
  url,
  queryKey
}: {
  dispatch: Dispatch<TableActionItemAction<TableData>>;
  isOpen: boolean;
  anchorEl: Element | null;
  itemId: number;
  url: string;
  queryKey: string;
}) => {
  const [noteSubject, setNoteSubject] = useState('');
  const [noteBody, setNoteBody] = useState('');

  const queryClient = useQueryClient();

  const handleClose = () => {
    dispatch({ type: 'CLOSE_ACTION_ITEM', payload: 'addNote' });
    setNoteSubject('');
    setNoteBody('');
  };

  const { mutate: saveNote, isLoading: savingNote } = useMutation({
    mutationFn: async () =>
      callAPI(
        url,
        {
          id: itemId,
          note: {
            subject: noteSubject,
            body: noteBody
          }
        },
        'POST'
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKey] });
      handleClose();
    }
  });

  return (
    <Popover
      anchorEl={anchorEl}
      id="edit-note-popover"
      onClose={handleClose}
      open={isOpen}
      sx={classes.editNotePopover}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right'
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right'
      }}
    >
      <>
        <Box sx={classes.notesTitle}>Add application note</Box>
        <TextField
          fullWidth
          required
          id="note-input"
          label="Subject"
          onChange={(e) => setNoteSubject(e.target.value)}
          sx={classes.noteInput}
          variant="outlined"
          slotProps={{
            inputLabel: {
              shrink: true
            }
          }}
        />
        <TextField
          fullWidth
          multiline
          id="note-input"
          label="Body"
          maxRows={4}
          onChange={(e) => setNoteBody(e.target.value)}
          sx={classes.noteInput}
          variant="outlined"
          slotProps={{
            inputLabel: {
              shrink: true
            }
          }}
        />
        <Box id="notes-button-container" sx={classes.notesButtonContainer}>
          <Button endIcon={<CloseIcon />} onClick={handleClose} sx={classes.notesCancelButton}>
            Cancel
          </Button>
          <Button
            disabled={!noteSubject || !!savingNote}
            endIcon={savingNote ? <CircularProgress size={16} /> : <DoneIcon />}
            sx={classes.notesEditSaveButton}
            onClick={() => {
              saveNote();
            }}
          >
            Save
          </Button>
        </Box>
      </>
    </Popover>
  );
};

export default AddNoteWithSubjectLine;
